import type { ApiError } from "../../api/result.ts";
import type {
  Capability,
  DraftRelevancy,
  RelevancyFillView,
  RelevancyRating,
} from "../../api/domain.ts";
import type { Coverage } from "../../lib/format.ts";

/**
 * The relevancy fill page's state, shaped exactly like the answer page's.
 *
 * Both pages face the same four situations, and the server answers one status
 * for an unknown, an inactive and an already evaluated link on purpose, so
 * "dead" stays one state here too.
 */
export type RelevancyState =
  | { state: "loading" }
  | { state: "open"; view: RelevancyFillView }
  | { state: "dead"; reason: DeadReason }
  | { state: "failed"; error: ApiError };

export type DeadReason = "unavailable" | "submitted";

/**
 * Autosave state of the whole page: one badge, not one per row, because the
 * recipient wants to know "is my work safe", not which click made it so.
 */
export type SaveState =
  | { state: "idle" }
  | { state: "saving" }
  | { state: "saved"; at: number }
  | { state: "failed"; error: ApiError };

/**
 * A 401 on a token-scoped route means the link is unknown, inactive or already
 * evaluated; anything else is a real failure the reader can retry.
 */
export const deadOrFailed = (error: ApiError): RelevancyState =>
  error.kind === "server" && error.envelope.kind === "unauthorized"
    ? { state: "dead", reason: "unavailable" }
    : { state: "failed", error };

/** A rating is identified by its capability, since the persona is fixed. */
export const ratingOf = (
  drafts: DraftRelevancy[],
  capability: string,
): RelevancyRating | undefined =>
  drafts.find((draft) => draft.capability === capability)?.rating ?? undefined;

/** The note left on a capability, null while none was written. */
export const noteOf = (drafts: DraftRelevancy[], capability: string): string | null =>
  drafts.find((draft) => draft.capability === capability)?.note ?? null;

/** What one save changes; an absent field keeps what the draft already holds. */
export type DraftPatch = {
  rating?: RelevancyRating;
  note?: string | null;
};

/**
 * Merges a patch into the draft for one capability, mirroring what the server
 * does with a partial body. A pick and a note are saved on separate clocks, so
 * neither may overwrite the other on the way in.
 */
export const upsertDraft = (
  drafts: DraftRelevancy[],
  questionnaire: string,
  capability: string,
  patch: DraftPatch,
): DraftRelevancy[] => {
  const existing = drafts.find((candidate) => candidate.capability === capability);
  const merged: DraftRelevancy = {
    questionnaire,
    capability,
    rating: null,
    note: null,
    ...existing,
    ...patch,
  };
  if (existing === undefined) return [...drafts, merged];
  return drafts.map((candidate) => (candidate.capability === capability ? merged : candidate));
};

/** Every capability in scope, flattened out of its topic groups. */
export const allCapabilities = (view: RelevancyFillView): Capability[] =>
  view.groups.flatMap((group) => group.capabilities);

/**
 * Rated against total. An unrated capability is a gap, not a neutral answer:
 * the form never preselects a step, so progress counts only what was picked --
 * a draft that carries a note alone is not an answer.
 */
export const progressOf = (view: RelevancyFillView): Coverage => {
  const all = allCapabilities(view);

  return {
    answered: all.filter(
      (capability) => ratingOf(view.drafts, capability.id) !== undefined,
    ).length,
    total: all.length,
  };
};

export type TerminalCopy = {
  title: string;
  body: string;
};
